import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  TrendingUp,
  TrendingDown,
  Filter,
  X,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ArrowUpCircle,
  Zap,
  Target,
  Play,
  Brain,
  Trash2,
} from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { bankrollApi, calibrationApi } from '../../api/client';
import type { CalibrationRunMetrics } from '../../api/client';
import type { Bet, BankrollSnapshot, BetMarket, CalibrationMetrics, CalibrationStatus, CandidateWeightComparison } from '../../types';
import { DiamondEdgeSubNav } from '../../components/DiamondEdge/DiamondEdgeSubNav';
import { LoadingScreen } from '../../components/common/Spinner';
import { TeamLogo } from '../../components/common/TeamLogo';
import { PaginationControls } from '../../components/common/PaginationControls';
import {
  formatCurrency,
  formatPct,
  formatROI,
  formatPnl,
  formatMarket,
  formatOdds,
  getStatusColor,
} from '../../utils/format';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartTooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
  Legend,
  ComposedChart,
  Area,
} from 'recharts';

// ── Constants ─────────────────────────────────

const TT = {
  contentStyle: {
    background: '#0f172a',
    border: '1px solid #1e293b',
    borderRadius: 6,
    fontSize: 11,
  },
  labelStyle: { color: '#94a3b8' },
  itemStyle: { color: '#e2e8f0' },
  cursor: { fill: 'rgba(255,255,255,0.03)' },
};

const MARKETS: BetMarket[] = [
  'moneyline',
  'runline',
  'total',
  'first5_moneyline',
  'first5_total',
  'first5_runline',
  'live_moneyline',
  'live_total',
];
const CONF_LEVELS = ['high', 'medium', 'low'] as const;
const RESULTS = ['won', 'lost', 'push', 'void'] as const;

// ── Edge / confidence helpers ─────────────────
// Bets placed before the stake-modal update may have edge/confidence stored as NULL.
// Fall back to derived values so analytics remain meaningful.

function betEdge(b: Bet): number | undefined {
  if (b.edge !== undefined) return b.edge;
  if (b.modelProbability !== undefined) return b.modelProbability - b.impliedProbability;
  return undefined;
}

function betConfTier(b: Bet): 'high' | 'medium' | 'low' {
  if (b.confidence !== undefined) {
    return b.confidence >= 0.7 ? 'high' : b.confidence >= 0.5 ? 'medium' : 'low';
  }
  // Infer from derived edge when confidence wasn't stored
  const e = betEdge(b);
  if (e === undefined) return 'low';
  return e >= 0.07 ? 'high' : e >= 0.04 ? 'medium' : 'low';
}

// ── Filter types ──────────────────────────────

interface Filters {
  dateStart: string;
  dateEnd: string;
  markets: BetMarket[];
  results: string[];
  confidence: string[];
  minOdds: string;
  maxOdds: string;
}

const DEFAULT_FILTERS: Filters = {
  dateStart: '',
  dateEnd: '',
  markets: [],
  results: [],
  confidence: [],
  minOdds: '',
  maxOdds: '',
};

function countActiveFilters(f: Filters): number {
  return [
    f.dateStart || f.dateEnd,
    f.markets.length > 0,
    f.results.length > 0,
    f.confidence.length > 0,
    f.minOdds || f.maxOdds,
  ].filter(Boolean).length;
}

function applyFilters(bets: Bet[], f: Filters): Bet[] {
  return bets.filter((b) => {
    if (f.dateStart && b.gameDate < f.dateStart) return false;
    if (f.dateEnd && b.gameDate > f.dateEnd) return false;
    if (f.markets.length && !f.markets.includes(b.market)) return false;
    if (f.results.length && !f.results.includes(b.result ?? '')) return false;
    if (f.confidence.length && b.confidence !== undefined) {
      const lvl = b.confidence >= 0.7 ? 'high' : b.confidence >= 0.5 ? 'medium' : 'low';
      if (!f.confidence.includes(lvl)) return false;
    }
    if (f.minOdds && b.odds < parseFloat(f.minOdds)) return false;
    if (f.maxOdds && b.odds > parseFloat(f.maxOdds)) return false;
    return true;
  });
}

// ── Stats computation ─────────────────────────

function computeStats(bets: Bet[]) {
  const settled = bets.filter((b) => ['won', 'lost', 'push', 'void'].includes(b.status));
  const wins = settled.filter((b) => b.result === 'won');
  const losses = settled.filter((b) => b.result === 'lost');
  const pushes = settled.filter((b) => b.result === 'push');
  const netProfit = settled.reduce((s, b) => s + (b.pnl ?? 0), 0);
  const totalStaked = settled.reduce((s, b) => s + b.stakeAmount, 0);
  const roi = totalStaked > 0 ? netProfit / totalStaked : 0;
  const winRate = settled.length > 0 ? wins.length / settled.length : 0;
  const avgOdds = settled.length > 0 ? settled.reduce((s, b) => s + b.odds, 0) / settled.length : 0;
  const edgeBets = bets.filter((b) => betEdge(b) !== undefined);
  const avgEdge =
    edgeBets.length > 0 ? edgeBets.reduce((s, b) => s + (betEdge(b) ?? 0), 0) / edgeBets.length : 0;
  const avgStake = settled.length > 0 ? totalStaked / settled.length : 0;

  const byMarket: Record<string, { bets: number; profit: number; staked: number; wins: number }> =
    {};
  for (const b of settled) {
    if (!byMarket[b.market]) byMarket[b.market] = { bets: 0, profit: 0, staked: 0, wins: 0 };
    byMarket[b.market].bets++;
    byMarket[b.market].profit += b.pnl ?? 0;
    byMarket[b.market].staked += b.stakeAmount;
    if (b.result === 'won') byMarket[b.market].wins++;
  }

  const byConf: Record<string, { bets: number; profit: number; staked: number; wins: number }> = {
    high: { bets: 0, profit: 0, staked: 0, wins: 0 },
    medium: { bets: 0, profit: 0, staked: 0, wins: 0 },
    low: { bets: 0, profit: 0, staked: 0, wins: 0 },
  };
  for (const b of settled) {
    const lvl = betConfTier(b);
    byConf[lvl].bets++;
    byConf[lvl].profit += b.pnl ?? 0;
    byConf[lvl].staked += b.stakeAmount;
    if (b.result === 'won') byConf[lvl].wins++;
  }

  const oddsBins = [
    { label: '<-200', min: -Infinity, max: -200 },
    { label: '-200 to -150', min: -200, max: -150 },
    { label: '-150 to -110', min: -150, max: -110 },
    { label: '-110 to +100', min: -110, max: 100 },
    { label: '+100 to +150', min: 100, max: 150 },
    { label: '+150 to +200', min: 150, max: 200 },
    { label: '>+200', min: 200, max: Infinity },
  ];
  const oddsHist = oddsBins
    .map(({ label, min, max }) => {
      const bin = settled.filter((b) => b.odds > min && b.odds <= max);
      const binWins = bin.filter((b) => b.result === 'won').length;
      const binProfit = bin.reduce((s, b) => s + (b.pnl ?? 0), 0);
      const binStaked = bin.reduce((s, b) => s + b.stakeAmount, 0);
      return {
        label,
        count: bin.length,
        winRate: bin.length > 0 ? binWins / bin.length : 0,
        roi: binStaked > 0 ? binProfit / binStaked : 0,
        profit: binProfit,
      };
    })
    .filter((b) => b.count > 0);

  const clvBets = settled.filter((b) => b.clv != null);
  const avgCLV =
    clvBets.length > 0 ? clvBets.reduce((s, b) => s + (b.clv ?? 0), 0) / clvBets.length : undefined;
  const clvSampleSize = clvBets.length;

  return {
    total: bets.length,
    settled: settled.length,
    wins: wins.length,
    losses: losses.length,
    pushes: pushes.length,
    netProfit,
    totalStaked,
    roi,
    winRate,
    avgOdds,
    avgEdge,
    avgStake,
    avgCLV,
    clvSampleSize,
    byMarket,
    byConf,
    oddsHist,
  };
}

// ── EV / Skill-vs-Luck ────────────────────────

function computeEVData(bets: Bet[]) {
  const settled = bets
    .filter((b) => ['won', 'lost', 'push', 'void'].includes(b.status) && betEdge(b) !== undefined)
    .sort((a, b) => a.gameDate.localeCompare(b.gameDate));
  if (settled.length === 0) return [];

  const byDate: Record<string, { ev: number; actual: number }> = {};
  for (const b of settled) {
    if (!byDate[b.gameDate]) byDate[b.gameDate] = { ev: 0, actual: 0 };
    byDate[b.gameDate].ev += (betEdge(b) ?? 0) * b.stakeAmount;
    byDate[b.gameDate].actual += b.pnl ?? 0;
  }

  let cumEV = 0,
    cumActual = 0;
  return Object.entries(byDate).map(([date, v]) => {
    cumEV += v.ev;
    cumActual += v.actual;
    return { date, ev: Math.round(cumEV * 100) / 100, actual: Math.round(cumActual * 100) / 100 };
  });
}

// ── Decision Quality Score ────────────────────

function scoreDecision(bet: Bet): number {
  let score = 0;
  const edge = betEdge(bet) ?? 0;
  if (edge >= 0.07) score += 40;
  else if (edge >= 0.05) score += 31;
  else if (edge >= 0.03) score += 22;
  else if (edge >= 0.01) score += 13;
  else if (edge > 0) score += 6;

  const conf = bet.confidence ?? 0;
  if (conf >= 0.75) score += 35;
  else if (conf >= 0.65) score += 27;
  else if (conf >= 0.5) score += 18;
  else if (conf > 0) score += 9;
  else if (betEdge(bet) !== undefined) score += 5; // Derived-only: partial credit

  if (bet.recommendationId) score += 15;
  else score += 5;
  if (bet.odds >= -200 && bet.odds <= 350) score += 10;
  else score += 4;

  return Math.min(100, score);
}

function dqCategory(score: number): { label: string; color: string; bg: string } {
  if (score >= 75) return { label: 'Elite', color: 'text-win', bg: 'bg-win/15' };
  if (score >= 55) return { label: 'Good', color: 'text-brand', bg: 'bg-brand/15' };
  if (score >= 35) return { label: 'Neutral', color: 'text-gray-400', bg: 'bg-surface-3' };
  return { label: 'Poor', color: 'text-loss', bg: 'bg-loss/15' };
}

// ── Strategy Simulator ────────────────────────

type StakingMethod = 'flat' | 'half_kelly' | 'quarter_kelly';

interface SimSettings {
  confFilter: 'all' | 'high' | 'high_medium';
  minEdge: number;
  staking: StakingMethod;
  unitSize: number;
}

function runSimulation(bets: Bet[], settings: SimSettings, startBankroll: number) {
  const eligible = bets
    .filter((b) => ['won', 'lost', 'push', 'void'].includes(b.status))
    .filter((b) => {
      if (settings.confFilter === 'high' && betConfTier(b) !== 'high') return false;
      if (settings.confFilter === 'high_medium' && betConfTier(b) === 'low') return false;
      return true;
    })
    .filter((b) => (betEdge(b) ?? 0) >= settings.minEdge)
    .sort((a, b) => a.gameDate.localeCompare(b.gameDate));

  let bankroll = startBankroll;
  let peak = bankroll;
  let maxDD = 0;
  let totalStaked = 0;
  const curve: { idx: number; sim: number; date: string }[] = [
    { idx: 0, sim: bankroll, date: 'Start' },
  ];

  for (const bet of eligible) {
    let stake: number;
    if (settings.staking === 'flat') {
      stake = settings.unitSize;
    } else {
      const wp =
        bet.modelProbability ?? Math.min(0.95, bet.impliedProbability + (betEdge(bet) ?? 0));
      const dec = bet.odds > 0 ? bet.odds / 100 + 1 : 100 / Math.abs(bet.odds) + 1;
      const net = dec - 1;
      const kelly = Math.max(0, (net * wp - (1 - wp)) / net);
      const frac = settings.staking === 'half_kelly' ? 0.5 : 0.25;
      stake = Math.min(bankroll * kelly * frac, bankroll * 0.2, bankroll);
    }
    stake = Math.max(0.01, Math.min(stake, bankroll));
    totalStaked += stake;

    if (bet.result === 'won') {
      const dec = bet.odds > 0 ? bet.odds / 100 + 1 : 100 / Math.abs(bet.odds) + 1;
      bankroll += stake * (dec - 1);
    } else if (bet.result === 'lost') {
      bankroll -= stake;
    }
    bankroll = Math.max(0, Math.round(bankroll * 100) / 100);
    if (bankroll > peak) peak = bankroll;
    const dd = peak > 0 ? (peak - bankroll) / peak : 0;
    if (dd > maxDD) maxDD = dd;
    curve.push({ idx: curve.length, sim: bankroll, date: bet.gameDate });
  }

  const wins = eligible.filter((b) => b.result === 'won').length;
  return {
    betsUsed: eligible.length,
    finalBankroll: bankroll,
    netProfit: bankroll - startBankroll,
    roi: totalStaked > 0 ? (bankroll - startBankroll) / totalStaked : 0,
    winRate: eligible.length > 0 ? wins / eligible.length : 0,
    maxDrawdown: maxDD,
    curve,
  };
}

// ── Insight generation (enhanced) ────────────

type Insight = { text: string; type: 'positive' | 'negative' | 'neutral' | 'warning' };

function generateInsights(bets: Bet[]): Insight[] {
  const insights: Insight[] = [];
  const settled = bets.filter((b) => ['won', 'lost', 'push', 'void'].includes(b.status));

  if (settled.length < 3) {
    insights.push({
      text: `Only ${settled.length} settled bet${settled.length !== 1 ? 's' : ''} — build your sample size for reliable conclusions.`,
      type: 'warning',
    });
    return insights;
  }

  const roiOf = (arr: Bet[]) => {
    const p = arr.reduce((s, b) => s + (b.pnl ?? 0), 0);
    const st = arr.reduce((s, b) => s + b.stakeAmount, 0);
    return st > 0 ? p / st : 0;
  };

  // Market analysis
  const byMkt = Object.entries(
    settled.reduce<Record<string, Bet[]>>((acc, b) => {
      acc[b.market] = [...(acc[b.market] ?? []), b];
      return acc;
    }, {}),
  )
    .filter(([, v]) => v.length >= 2)
    .map(([k, v]) => ({ market: k, roi: roiOf(v), bets: v.length }))
    .sort((a, b) => b.roi - a.roi);

  if (byMkt.length >= 2) {
    const best = byMkt[0];
    if (best.roi > 0.02)
      insights.push({
        text: `${formatMarket(best.market as BetMarket)} is your strongest market at ${(best.roi * 100).toFixed(1)}% ROI (${best.bets} bets)`,
        type: 'positive',
      });
    const worst = byMkt[byMkt.length - 1];
    if (worst.roi < -0.05)
      insights.push({
        text: `${formatMarket(worst.market as BetMarket)} is dragging performance at ${(worst.roi * 100).toFixed(1)}% ROI — consider reducing exposure`,
        type: 'negative',
      });
  }

  // Favorites vs underdogs
  const favs = settled.filter((b) => b.odds < -100);
  const dogs = settled.filter((b) => b.odds >= 100);
  if (favs.length >= 3 && dogs.length >= 3) {
    const fROI = roiOf(favs),
      dROI = roiOf(dogs);
    if (dROI > fROI + 0.05)
      insights.push({
        text: `Underdog bets outperforming favorites: ${(dROI * 100).toFixed(1)}% vs ${(fROI * 100).toFixed(1)}% ROI`,
        type: 'positive',
      });
    else if (fROI > dROI + 0.05)
      insights.push({
        text: `Favorite bets outperforming underdogs: ${(fROI * 100).toFixed(1)}% vs ${(dROI * 100).toFixed(1)}% ROI`,
        type: 'positive',
      });
  }

  // Confidence correlation
  const hiC = settled.filter((b) => betConfTier(b) === 'high');
  const loC = settled.filter((b) => betConfTier(b) === 'low');
  if (hiC.length >= 2 && loC.length >= 2) {
    const hROI = roiOf(hiC),
      lROI = roiOf(loC);
    if (hROI > lROI + 0.05)
      insights.push({
        text: `Model confidence is predictive: high-confidence bets at ${(hROI * 100).toFixed(1)}% vs ${(lROI * 100).toFixed(1)}% ROI for low`,
        type: 'positive',
      });
    else if (lROI > hROI + 0.05)
      insights.push({
        text: `Low-confidence bets outperforming high (${(lROI * 100).toFixed(1)}% vs ${(hROI * 100).toFixed(1)}% ROI) — investigate model calibration`,
        type: 'neutral',
      });
  }

  // Stake creep detection
  const ordered = [...settled].sort(
    (a, b) => a.gameDate.localeCompare(b.gameDate) || a.createdAt.localeCompare(b.createdAt),
  );
  let consecLosses = 0;
  for (let i = 1; i < ordered.length; i++) {
    if (ordered[i - 1].result === 'lost') consecLosses++;
    else consecLosses = 0;
    if (consecLosses >= 2 && ordered[i].stakeAmount > ordered[i - 1].stakeAmount * 1.4) {
      insights.push({
        text: 'Stake sizes appear to increase after losing streaks — potential chasing behavior detected',
        type: 'warning',
      });
      break;
    }
  }

  // Recent form vs all-time
  const recent = ordered.slice(-Math.min(10, ordered.length));
  if (recent.length >= 5) {
    const recROI = roiOf(recent),
      allROI = roiOf(settled);
    if (recROI > allROI + 0.1)
      insights.push({
        text: `Recent form is strong: last ${recent.length} bets at ${(recROI * 100).toFixed(1)}% ROI vs ${(allROI * 100).toFixed(1)}% all-time`,
        type: 'positive',
      });
    else if (recROI < allROI - 0.1)
      insights.push({
        text: `Form has cooled recently: last ${recent.length} bets at ${(recROI * 100).toFixed(1)}% ROI vs ${(allROI * 100).toFixed(1)}% all-time`,
        type: 'warning',
      });
  }

  // Team overexposure
  const selCount: Record<string, number> = {};
  for (const b of settled) selCount[b.selection] = (selCount[b.selection] ?? 0) + 1;
  const overexp = Object.entries(selCount)
    .filter(([, v]) => v >= 3)
    .sort((a, b) => b[1] - a[1]);
  if (overexp.length > 0) {
    const [team, cnt] = overexp[0];
    const teamBets = settled.filter((b) => b.selection === team);
    const tROI = roiOf(teamBets);
    if (tROI < -0.05)
      insights.push({
        text: `You've bet on ${team} ${cnt} times at ${(tROI * 100).toFixed(1)}% ROI — concentrated exposure to an underperforming selection`,
        type: 'negative',
      });
  }

  // Day-of-week patterns
  const byDay: Record<string, Bet[]> = {};
  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  for (const b of settled) {
    const day = DAYS[new Date(b.gameDate + 'T12:00:00').getDay()];
    byDay[day] = [...(byDay[day] ?? []), b];
  }
  const dayROIs = Object.entries(byDay)
    .filter(([, v]) => v.length >= 3)
    .map(([day, arr]) => ({ day, roi: roiOf(arr), count: arr.length }))
    .sort((a, b) => b.roi - a.roi);
  if (dayROIs.length >= 2 && dayROIs[0].roi - dayROIs[dayROIs.length - 1].roi > 0.1) {
    insights.push({
      text: `Best results on ${dayROIs[0].day}s (${(dayROIs[0].roi * 100).toFixed(1)}% ROI), worst on ${dayROIs[dayROIs.length - 1].day}s (${(dayROIs[dayROIs.length - 1].roi * 100).toFixed(1)}% ROI)`,
      type: 'neutral',
    });
  }

  // CLV signal
  const clvBets = settled.filter((b) => b.clv != null);
  if (clvBets.length >= 5) {
    const avgCLV = clvBets.reduce((s, b) => s + (b.clv ?? 0), 0) / clvBets.length;
    const clvPct = (avgCLV * 100).toFixed(2);
    if (avgCLV > 0.005) {
      insights.push({
        text: `Positive CLV of +${clvPct}pp across ${clvBets.length} tracked bets — you're consistently beating the closing line, a strong signal of genuine edge`,
        type: 'positive',
      });
    } else if (avgCLV < -0.005) {
      insights.push({
        text: `Negative CLV of ${clvPct}pp across ${clvBets.length} tracked bets — recent wins may be variance; closing line is beating your entry price`,
        type: 'warning',
      });
    } else {
      insights.push({
        text: `CLV near zero (${clvPct}pp, ${clvBets.length} bets) — you're roughly matching the closing line; need more sample to distinguish skill from variance`,
        type: 'neutral',
      });
    }
  } else if (clvBets.length > 0) {
    insights.push({
      text: `${clvBets.length} bet${clvBets.length !== 1 ? 's' : ''} with closing odds recorded — enter closing odds at settlement to build your CLV baseline (need 5+ for signal)`,
      type: 'neutral',
    });
  } else {
    insights.push({
      text: "No CLV data yet — enter closing odds when settling bets to track whether you're beating the market's final price",
      type: 'warning',
    });
  }

  // Overall health
  const totalROI = roiOf(settled);
  if (settled.length >= 5 && totalROI > 0.05)
    insights.push({
      text: `Strong overall edge: ${(totalROI * 100).toFixed(1)}% ROI across ${settled.length} settled bets`,
      type: 'positive',
    });
  if (settled.length >= 5 && totalROI < -0.1)
    insights.push({
      text: `Below-expectation performance at ${(totalROI * 100).toFixed(1)}% ROI — review bet selection criteria`,
      type: 'negative',
    });

  if (insights.length === 0)
    insights.push({
      text: `${settled.length} settled bets with mixed signals. Continue building sample size for clearer patterns.`,
      type: 'neutral',
    });

  return insights;
}

// ── Shared helpers ────────────────────────────

function Chip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="flex items-center gap-1 bg-brand/10 border border-brand/30 text-brand text-xs rounded px-2 py-0.5">
      {label}
      <button onClick={onRemove}>
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

// ── Filter bar ────────────────────────────────

function FilterBar({ filters, onChange }: { filters: Filters; onChange: (f: Filters) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const count = countActiveFilters(filters);

  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  const toggle = <T,>(arr: T[], val: T): T[] =>
    arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];

  return (
    <div ref={ref} className="relative">
      <div className="flex items-center gap-2">
        <button
          onClick={() => setOpen((o) => !o)}
          className={clsx(
            'flex items-center gap-1.5 text-xs rounded px-3 py-1.5 border transition-colors',
            count > 0
              ? 'bg-brand/15 border-brand/50 text-brand'
              : 'bg-surface-1 border-surface-4 text-gray-400 hover:text-gray-200',
          )}
        >
          <Filter className="w-3.5 h-3.5" />
          Filters
          {count > 0 && (
            <span className="bg-brand text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center">
              {count}
            </span>
          )}
          <ChevronDown className={clsx('w-3 h-3 transition-transform', open && 'rotate-180')} />
        </button>
        {filters.markets.map((m) => (
          <Chip
            key={m}
            label={formatMarket(m)}
            onRemove={() =>
              onChange({ ...filters, markets: filters.markets.filter((x) => x !== m) })
            }
          />
        ))}
        {filters.results.map((r) => (
          <Chip
            key={r}
            label={r}
            onRemove={() =>
              onChange({ ...filters, results: filters.results.filter((x) => x !== r) })
            }
          />
        ))}
        {filters.confidence.map((c) => (
          <Chip
            key={c}
            label={`${c} conf`}
            onRemove={() =>
              onChange({ ...filters, confidence: filters.confidence.filter((x) => x !== c) })
            }
          />
        ))}
        {(filters.dateStart || filters.dateEnd) && (
          <Chip
            label={`${filters.dateStart || '…'} – ${filters.dateEnd || '…'}`}
            onRemove={() => onChange({ ...filters, dateStart: '', dateEnd: '' })}
          />
        )}
        {count > 0 && (
          <button
            onClick={() => onChange(DEFAULT_FILTERS)}
            className="text-xs text-gray-600 hover:text-gray-400 ml-1"
          >
            Reset all
          </button>
        )}
      </div>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-surface-1 border border-surface-4 rounded-lg shadow-xl p-4 w-[520px]">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-gray-600 mb-2">
                Date Range
              </div>
              <input
                type="date"
                value={filters.dateStart}
                max={filters.dateEnd || undefined}
                onChange={(e) => onChange({ ...filters, dateStart: e.target.value })}
                className="w-full bg-surface-3 border border-surface-4 focus:border-brand rounded px-2 py-1 text-xs text-gray-300 outline-none mb-1"
              />
              <input
                type="date"
                value={filters.dateEnd}
                min={filters.dateStart || undefined}
                onChange={(e) => onChange({ ...filters, dateEnd: e.target.value })}
                className="w-full bg-surface-3 border border-surface-4 focus:border-brand rounded px-2 py-1 text-xs text-gray-300 outline-none"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-gray-600 mb-2">Market</div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {MARKETS.map((m) => (
                  <label key={m} className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={filters.markets.includes(m)}
                      onChange={() => onChange({ ...filters, markets: toggle(filters.markets, m) })}
                      className="accent-brand"
                    />
                    <span
                      className={clsx(
                        'text-xs transition-colors',
                        filters.markets.includes(m)
                          ? 'text-brand'
                          : 'text-gray-500 group-hover:text-gray-300',
                      )}
                    >
                      {formatMarket(m)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-600 mb-2">Result</div>
                <div className="flex flex-wrap gap-1">
                  {RESULTS.map((r) => (
                    <button
                      key={r}
                      onClick={() => onChange({ ...filters, results: toggle(filters.results, r) })}
                      className={clsx(
                        'px-2 py-0.5 text-xs rounded capitalize border transition-colors',
                        filters.results.includes(r)
                          ? 'bg-brand/20 border-brand/50 text-brand'
                          : 'border-surface-4 text-gray-500 hover:text-gray-300',
                      )}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-600 mb-2">
                  Confidence
                </div>
                <div className="flex flex-wrap gap-1">
                  {CONF_LEVELS.map((c) => (
                    <button
                      key={c}
                      onClick={() =>
                        onChange({ ...filters, confidence: toggle(filters.confidence, c) })
                      }
                      className={clsx(
                        'px-2 py-0.5 text-xs rounded capitalize border transition-colors',
                        filters.confidence.includes(c)
                          ? 'bg-brand/20 border-brand/50 text-brand'
                          : 'border-surface-4 text-gray-500 hover:text-gray-300',
                      )}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-600 mb-2">
                  Odds Range
                </div>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.minOdds}
                    onChange={(e) => onChange({ ...filters, minOdds: e.target.value })}
                    className="w-full bg-surface-3 border border-surface-4 focus:border-brand rounded px-2 py-1 text-xs text-gray-300 outline-none"
                  />
                  <span className="text-gray-600 text-xs">–</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.maxOdds}
                    onChange={(e) => onChange({ ...filters, maxOdds: e.target.value })}
                    className="w-full bg-surface-3 border border-surface-4 focus:border-brand rounded px-2 py-1 text-xs text-gray-300 outline-none"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── KPI card ──────────────────────────────────

function KPICard({
  label,
  value,
  sub,
  accent,
  negative,
  tooltip,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
  negative?: boolean;
  tooltip?: string;
}) {
  const [showTip, setShowTip] = useState(false);
  return (
    <div
      onMouseEnter={() => setShowTip(true)}
      onMouseLeave={() => setShowTip(false)}
      className="op-panel p-3 relative"
    >
      <div className="stat-label flex items-center gap-1">
        {label}
        {tooltip && <AlertCircle className="w-3 h-3 text-gray-700" />}
      </div>
      <div
        className={clsx(
          'text-xl font-bold font-mono mt-1',
          negative ? 'text-loss' : accent ? 'text-win' : 'text-gray-200',
        )}
      >
        {value}
      </div>
      {sub && <div className="text-[10px] text-gray-600 mt-0.5">{sub}</div>}
      {showTip && tooltip && (
        <div className="absolute bottom-full left-0 mb-1 z-50 bg-surface-0 border border-surface-4 rounded px-2 py-1.5 text-xs text-gray-400 w-48 shadow-lg pointer-events-none">
          {tooltip}
        </div>
      )}
    </div>
  );
}

// ── Skill vs Luck chart ───────────────────────

function SkillVsLuckChart({ bets }: { bets: Bet[] }) {
  const data = useMemo(() => computeEVData(bets), [bets]);
  if (data.length < 2) return null;

  const last = data[data.length - 1];
  const gap = last.actual - last.ev;
  const status = gap > 0.5 ? 'above' : gap < -0.5 ? 'below' : 'aligned';

  return (
    <div className="op-panel p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-brand" />
          <span className="text-sm font-medium text-gray-300">Skill vs Luck</span>
        </div>
        <div
          className={clsx(
            'text-xs font-mono px-2 py-0.5 rounded border',
            status === 'above'
              ? 'bg-win/10 border-win/30 text-win'
              : status === 'below'
                ? 'bg-loss/10 border-loss/30 text-loss'
                : 'bg-surface-3 border-surface-4 text-gray-400',
          )}
        >
          {status === 'above'
            ? `+${formatCurrency(gap)} above EV (running hot)`
            : status === 'below'
              ? `${formatCurrency(Math.abs(gap))} below EV (running cold)`
              : 'Aligned with expected value'}
        </div>
      </div>
      <div className="text-xs text-gray-600 mb-4">
        Dashed line = expected cumulative P&L (edge × stake). Solid line = actual. Gap is your luck
        component.
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={data}>
          <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#6b7280' }} tickLine={false} />
          <YAxis
            tick={{ fontSize: 9, fill: '#6b7280' }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => `$${v}`}
          />
          <RechartTooltip
            {...TT}
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const ev = (payload.find((p) => p.dataKey === 'ev')?.value as number) ?? 0;
              const actual = (payload.find((p) => p.dataKey === 'actual')?.value as number) ?? 0;
              return (
                <div style={TT.contentStyle} className="rounded p-2 space-y-1">
                  <div style={TT.labelStyle}>{label}</div>
                  <div className="text-brand">
                    EV: {ev >= 0 ? '+' : ''}
                    {formatCurrency(Math.abs(ev))}
                  </div>
                  <div className={actual >= 0 ? 'text-win' : 'text-loss'}>
                    Actual: {actual >= 0 ? '+' : ''}
                    {formatCurrency(Math.abs(actual))}
                  </div>
                  <div className="text-gray-500 border-t border-surface-4 pt-1 mt-1">
                    Luck: {actual - ev >= 0 ? '+' : ''}
                    {formatCurrency(actual - ev)}
                  </div>
                </div>
              );
            }}
          />
          <ReferenceLine y={0} stroke="#374151" />
          <Line
            type="monotone"
            dataKey="ev"
            stroke="#3b82f6"
            strokeWidth={1.5}
            dot={false}
            strokeDasharray="5 3"
            name="Expected (EV)"
          />
          <Line
            type="monotone"
            dataKey="actual"
            stroke={last.actual >= last.ev ? '#10b981' : '#ef4444'}
            strokeWidth={2}
            dot={false}
            name="Actual P&L"
          />
          <Legend iconType="line" iconSize={10} wrapperStyle={{ fontSize: 10, color: '#9ca3af' }} />
        </LineChart>
      </ResponsiveContainer>
      <div className="grid grid-cols-3 gap-3 text-center border-t border-surface-4 pt-3 mt-1">
        {[
          { label: 'Expected P&L', val: last.ev, color: 'text-brand' },
          {
            label: 'Actual P&L',
            val: last.actual,
            color: last.actual >= 0 ? 'text-win' : 'text-loss',
          },
          { label: 'Luck Factor', val: gap, color: gap >= 0 ? 'text-win' : 'text-loss' },
        ].map((item) => (
          <div key={item.label}>
            <div className="text-[10px] text-gray-600 uppercase tracking-wide">{item.label}</div>
            <div className={clsx('text-sm font-mono font-semibold mt-0.5', item.color)}>
              {item.val >= 0 ? '+' : ''}
              {formatCurrency(Math.abs(item.val))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Decision Quality panel ────────────────────

function DecisionQualityPanel({ bets }: { bets: Bet[] }) {
  const settled = bets.filter((b) => ['won', 'lost', 'push', 'void'].includes(b.status));
  const withEdge = settled.filter((b) => betEdge(b) !== undefined || b.confidence !== undefined);
  if (withEdge.length === 0) return null;

  const scored = withEdge.map((b) => ({ bet: b, score: scoreDecision(b) }));
  const avg = scored.reduce((s, x) => s + x.score, 0) / scored.length;

  const cats = { Elite: 0, Good: 0, Neutral: 0, Poor: 0 };
  for (const { score } of scored) cats[dqCategory(score).label as keyof typeof cats]++;

  // Correlation: high DQ score bets — did they win more?
  const highDQ = scored.filter((x) => x.score >= 65).map((x) => x.bet);
  const lowDQ = scored.filter((x) => x.score < 40).map((x) => x.bet);
  const winRateOf = (arr: Bet[]) =>
    arr.length > 0 ? arr.filter((b) => b.result === 'won').length / arr.length : 0;
  const highWR = winRateOf(highDQ);
  const lowWR = winRateOf(lowDQ);

  // Top 3 high-score bets that lost (reinforce process > outcome)
  const goodLosses = scored
    .filter((x) => x.score >= 65 && x.bet.result === 'lost')
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);

  const { color: avgColor } = dqCategory(avg);

  return (
    <div className="op-panel p-4">
      <div className="flex items-center gap-2 mb-4">
        <Target className="w-4 h-4 text-brand" />
        <span className="text-sm font-medium text-gray-300">Decision Quality</span>
        <span className="text-xs text-gray-600">— process score independent of outcomes</span>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        {/* Average score */}
        <div className="bg-surface-2 rounded-lg p-4 text-center">
          <div className="text-[10px] text-gray-600 uppercase tracking-wide mb-1">Avg Score</div>
          <div className={clsx('text-3xl font-bold font-mono', avgColor)}>{avg.toFixed(0)}</div>
          <div className={clsx('text-xs mt-1', avgColor)}>{dqCategory(avg).label}</div>
        </div>
        {/* Distribution */}
        <div className="space-y-2">
          {Object.entries(cats).map(([cat, cnt]) => {
            const { color, bg } = dqCategory(
              cat === 'Elite' ? 80 : cat === 'Good' ? 60 : cat === 'Neutral' ? 40 : 20,
            );
            const pct = withEdge.length > 0 ? (cnt / withEdge.length) * 100 : 0;
            return (
              <div key={cat} className="flex items-center gap-2">
                <span className={clsx('text-xs w-12 font-medium', color)}>{cat}</span>
                <div className="flex-1 h-2 bg-surface-4 rounded-full overflow-hidden">
                  <div className={clsx('h-full rounded-full', bg)} style={{ width: `${pct}%` }} />
                </div>
                <span className="text-xs text-gray-600 font-mono w-8 text-right">{cnt}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Win rate by DQ tier */}
      {highDQ.length >= 2 && lowDQ.length >= 2 && (
        <div className="bg-surface-2 rounded-lg p-3 mb-3 text-xs">
          <div className="text-gray-500 mb-2 font-medium">Quality vs Outcome Correlation</div>
          <div className="flex items-center gap-4">
            <div>
              <span className="text-win font-mono font-semibold">{(highWR * 100).toFixed(0)}%</span>
              <span className="text-gray-600 ml-1">win rate — high DQ bets ({highDQ.length})</span>
            </div>
            <div>
              <span className="text-loss font-mono font-semibold">{(lowWR * 100).toFixed(0)}%</span>
              <span className="text-gray-600 ml-1">win rate — low DQ bets ({lowDQ.length})</span>
            </div>
          </div>
          {highWR > lowWR + 0.05 ? (
            <div className="text-win mt-1.5">✓ Good process correlates with better outcomes</div>
          ) : highWR < lowWR - 0.05 ? (
            <div className="text-warn mt-1.5">
              ↻ Low-quality bets winning more — possible variance, monitor closely
            </div>
          ) : (
            <div className="text-gray-600 mt-1.5">
              Process quality and outcomes not yet correlated — keep building sample
            </div>
          )}
        </div>
      )}

      {/* Good decisions that lost */}
      {goodLosses.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-600 mb-2">
            Good Decisions That Lost (Process Was Right)
          </div>
          <div className="space-y-1.5">
            {goodLosses.map(({ bet, score }) => (
              <div
                key={bet.id}
                className="flex items-center justify-between text-xs bg-surface-2 rounded px-3 py-1.5"
              >
                <span className="text-gray-400">
                  {bet.selection} · {formatMarket(bet.market)} · {formatOdds(bet.odds)}
                </span>
                <div className="flex items-center gap-2 font-mono">
                  <span className="text-gray-600">{bet.gameDate}</span>
                  <span className="text-brand">DQ {score}</span>
                  <span className="text-loss">{formatPnl(bet.pnl ?? -bet.stakeAmount)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Strategy Simulator ────────────────────────

const SIM_DEFAULTS: SimSettings = { confFilter: 'all', minEdge: 0, staking: 'flat', unitSize: 10 };

function StrategySimulator({ bets, startBankroll }: { bets: Bet[]; startBankroll: number }) {
  const [settings, setSettings] = useState<SimSettings>(SIM_DEFAULTS);
  const settled = bets.filter((b) => ['won', 'lost', 'push', 'void'].includes(b.status));

  const sim = useMemo(
    () => runSimulation(bets, settings, startBankroll),
    [bets, settings, startBankroll],
  );

  // Actual trajectory from filtered bets
  const actualCurve = useMemo(() => {
    const ordered = [...settled].sort((a, b) => a.gameDate.localeCompare(b.gameDate));
    let bal = startBankroll;
    const pts: { idx: number; actual: number }[] = [{ idx: 0, actual: bal }];
    for (const b of ordered) {
      bal += b.pnl ?? 0;
      pts.push({ idx: pts.length, actual: Math.round(bal * 100) / 100 });
    }
    return pts;
  }, [settled, startBankroll]);

  // Merge curves by index
  const merged = useMemo(() => {
    const len = Math.max(sim.curve.length, actualCurve.length);
    return Array.from({ length: len }, (_, i) => ({
      idx: i,
      sim: sim.curve[i]?.sim ?? sim.curve[sim.curve.length - 1]?.sim,
      actual: actualCurve[i]?.actual ?? actualCurve[actualCurve.length - 1]?.actual,
      date: sim.curve[i]?.date ?? '',
    }));
  }, [sim.curve, actualCurve]);

  const actualProfit = settled.reduce((s, b) => s + (b.pnl ?? 0), 0);
  const actualStaked = settled.reduce((s, b) => s + b.stakeAmount, 0);
  const actualROI = actualStaked > 0 ? actualProfit / actualStaked : 0;
  const actualWR =
    settled.length > 0 ? settled.filter((b) => b.result === 'won').length / settled.length : 0;

  const set = (k: keyof SimSettings, v: unknown) => setSettings((s) => ({ ...s, [k]: v }));

  return (
    <div className="op-panel p-4">
      <div className="flex items-center gap-2 mb-4">
        <Play className="w-4 h-4 text-brand" />
        <span className="text-sm font-medium text-gray-300">
          &quot;What If?&quot; Strategy Simulator
        </span>
        <span className="text-xs text-gray-600">
          — replay your bets with different strategy settings
        </span>
        <button
          onClick={() => setSettings(SIM_DEFAULTS)}
          className="ml-auto text-xs text-gray-600 hover:text-gray-400"
        >
          Reset
        </button>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4 p-3 bg-surface-2 rounded-lg">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-600 mb-1.5">
            Confidence Filter
          </div>
          <select
            value={settings.confFilter}
            onChange={(e) => set('confFilter', e.target.value)}
            className="w-full bg-surface-3 border border-surface-4 focus:border-brand rounded px-2 py-1.5 text-xs text-gray-300 outline-none"
          >
            <option value="all">All bets</option>
            <option value="high_medium">High + Medium only</option>
            <option value="high">High confidence only</option>
          </select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-600 mb-1.5">Min Edge</div>
          <select
            value={settings.minEdge}
            onChange={(e) => set('minEdge', parseFloat(e.target.value))}
            className="w-full bg-surface-3 border border-surface-4 focus:border-brand rounded px-2 py-1.5 text-xs text-gray-300 outline-none"
          >
            <option value={0}>No minimum</option>
            <option value={0.02}>2%+ edge</option>
            <option value={0.03}>3%+ edge</option>
            <option value={0.05}>5%+ edge</option>
            <option value={0.07}>7%+ edge</option>
          </select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-600 mb-1.5">
            Staking Method
          </div>
          <select
            value={settings.staking}
            onChange={(e) => set('staking', e.target.value)}
            className="w-full bg-surface-3 border border-surface-4 focus:border-brand rounded px-2 py-1.5 text-xs text-gray-300 outline-none"
          >
            <option value="flat">Flat</option>
            <option value="half_kelly">½ Kelly</option>
            <option value="quarter_kelly">¼ Kelly</option>
          </select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-600 mb-1.5">
            {settings.staking === 'flat' ? 'Unit Size ($)' : 'Starting Bankroll ($)'}
          </div>
          <input
            type="number"
            min={1}
            value={settings.unitSize}
            onChange={(e) => set('unitSize', Math.max(1, parseFloat(e.target.value) || 1))}
            className="w-full bg-surface-3 border border-surface-4 focus:border-brand rounded px-2 py-1.5 text-xs text-gray-300 outline-none"
          />
        </div>
      </div>

      {/* Metrics comparison */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-surface-2 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wide text-gray-600 mb-2">
            Actual Results
          </div>
          <div className="space-y-1.5 text-xs">
            <Row2 label="Bets" val={String(settled.length)} />
            <Row2
              label="Net Profit"
              val={`${actualProfit >= 0 ? '+' : ''}${formatCurrency(actualProfit)}`}
              accent={actualProfit >= 0}
              negative={actualProfit < 0}
            />
            <Row2
              label="ROI"
              val={formatROI(actualROI)}
              accent={actualROI >= 0}
              negative={actualROI < 0}
            />
            <Row2 label="Win Rate" val={formatPct(actualWR)} />
          </div>
        </div>
        <div
          className={clsx(
            'rounded-lg p-3 border',
            sim.netProfit > actualProfit
              ? 'bg-win/5 border-win/20'
              : sim.netProfit < actualProfit
                ? 'bg-surface-2 border-surface-4'
                : 'bg-surface-2 border-surface-4',
          )}
        >
          <div className="text-[10px] uppercase tracking-wide text-gray-600 mb-2">
            Simulated <span className="text-gray-700">({sim.betsUsed} bets qualify)</span>
          </div>
          <div className="space-y-1.5 text-xs">
            <Row2 label="Bets Used" val={String(sim.betsUsed)} />
            <Row2
              label="Net Profit"
              val={`${sim.netProfit >= 0 ? '+' : ''}${formatCurrency(Math.abs(sim.netProfit))}`}
              accent={sim.netProfit >= 0}
              negative={sim.netProfit < 0}
            />
            <Row2
              label="ROI"
              val={formatROI(sim.roi)}
              accent={sim.roi >= 0}
              negative={sim.roi < 0}
            />
            <Row2 label="Win Rate" val={formatPct(sim.winRate)} />
            <Row2
              label="Max Drawdown"
              val={formatPct(sim.maxDrawdown)}
              negative={sim.maxDrawdown > 0.1}
            />
          </div>
          {sim.netProfit > actualProfit + 0.5 && (
            <div className="text-win text-[10px] mt-2">
              ↑ +{formatCurrency(sim.netProfit - actualProfit)} vs actual
            </div>
          )}
          {sim.netProfit < actualProfit - 0.5 && (
            <div className="text-loss text-[10px] mt-2">
              ↓ {formatCurrency(sim.netProfit - actualProfit)} vs actual
            </div>
          )}
        </div>
      </div>

      {/* Curves */}
      {merged.length > 2 && (
        <ResponsiveContainer width="100%" height={160}>
          <LineChart data={merged}>
            <XAxis dataKey="idx" hide />
            <YAxis
              tick={{ fontSize: 9, fill: '#6b7280' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => `$${v}`}
              domain={['auto', 'auto']}
            />
            <RechartTooltip
              {...TT}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0]?.payload;
                return (
                  <div style={TT.contentStyle} className="rounded p-2 space-y-1">
                    {d.date && <div style={TT.labelStyle}>{d.date}</div>}
                    <div className="text-brand">Sim: {formatCurrency(d.sim)}</div>
                    <div className="text-gray-300">Actual: {formatCurrency(d.actual)}</div>
                  </div>
                );
              }}
            />
            <ReferenceLine y={startBankroll} stroke="#374151" strokeDasharray="4 2" />
            <Line
              type="monotone"
              dataKey="actual"
              stroke="#6b7280"
              strokeWidth={1.5}
              dot={false}
              name="Actual"
            />
            <Line
              type="monotone"
              dataKey="sim"
              stroke={sim.netProfit >= actualProfit ? '#10b981' : '#3b82f6'}
              strokeWidth={2}
              dot={false}
              name="Simulated"
            />
            <Legend
              iconType="line"
              iconSize={10}
              wrapperStyle={{ fontSize: 10, color: '#9ca3af' }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
      {sim.betsUsed === 0 && (
        <div className="text-center text-xs text-gray-600 py-4">
          No bets qualify under current settings
        </div>
      )}
    </div>
  );
}

function Row2({
  label,
  val,
  accent,
  negative,
}: {
  label: string;
  val: string;
  accent?: boolean;
  negative?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-500">{label}</span>
      <span
        className={clsx(
          'font-mono font-medium',
          negative ? 'text-loss' : accent ? 'text-win' : 'text-gray-300',
        )}
      >
        {val}
      </span>
    </div>
  );
}

// ── Market chart ──────────────────────────────

type MarketView = 'profit' | 'roi' | 'bets';

function MarketChart({
  byMarket,
  onFilter,
  activeMarket,
}: {
  byMarket: Record<string, { bets: number; profit: number; staked: number; wins: number }>;
  onFilter: (m: string | null) => void;
  activeMarket: string | null;
}) {
  const [view, setView] = useState<MarketView>('profit');
  const data = Object.entries(byMarket)
    .filter(([, v]) => v.bets > 0)
    .map(([k, v]) => ({
      market: formatMarket(k as BetMarket),
      key: k,
      profit: Math.round(v.profit * 100) / 100,
      roi: v.staked > 0 ? Math.round((v.profit / v.staked) * 1000) / 10 : 0,
      bets: v.bets,
      winRate: v.bets > 0 ? Math.round((v.wins / v.bets) * 100) : 0,
    }))
    .sort((a, b) =>
      view === 'profit' ? b.profit - a.profit : view === 'roi' ? b.roi - a.roi : b.bets - a.bets,
    );

  return (
    <div className="op-panel p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm font-medium text-gray-300">Profit by Market</div>
        <div className="flex items-center gap-1">
          {(['profit', 'roi', 'bets'] as MarketView[]).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={clsx(
                'px-2 py-0.5 text-xs rounded transition-colors',
                view === v ? 'bg-brand text-white' : 'text-gray-500 hover:text-gray-300',
              )}
            >
              {v === 'roi' ? 'ROI %' : v === 'bets' ? 'Volume' : 'Profit $'}
            </button>
          ))}
        </div>
      </div>
      {data.length === 0 ? (
        <div className="h-32 flex items-center justify-center text-xs text-gray-600">
          No settled bets
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={150}>
          <BarChart
            data={data}
            barSize={28}
            onClick={(e) =>
              e?.activePayload &&
              onFilter(
                activeMarket === e.activePayload[0]?.payload?.key
                  ? null
                  : e.activePayload[0]?.payload?.key,
              )
            }
          >
            <XAxis dataKey="market" tick={{ fontSize: 9, fill: '#6b7280' }} tickLine={false} />
            <YAxis tick={{ fontSize: 9, fill: '#6b7280' }} tickLine={false} axisLine={false} />
            <RechartTooltip
              {...TT}
              formatter={(val: number) =>
                view === 'roi'
                  ? `${val.toFixed(1)}%`
                  : view === 'bets'
                    ? `${val} bets`
                    : formatCurrency(val)
              }
            />
            <ReferenceLine y={0} stroke="#374151" />
            <Bar dataKey={view === 'bets' ? 'bets' : view} cursor="pointer">
              {data.map((e) => (
                <Cell
                  key={e.key}
                  fill={
                    activeMarket && activeMarket !== e.key
                      ? '#374151'
                      : view === 'bets'
                        ? '#3b82f6'
                        : e[view === 'roi' ? 'roi' : 'profit'] >= 0
                          ? '#10b981'
                          : '#ef4444'
                  }
                  opacity={0.85}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
      <div className="mt-2 space-y-1">
        {data.map((m) => (
          <button
            key={m.key}
            onClick={() => onFilter(activeMarket === m.key ? null : m.key)}
            className={clsx(
              'w-full flex items-center justify-between text-xs rounded px-2 py-1 transition-colors',
              activeMarket === m.key
                ? 'bg-brand/10 text-brand'
                : 'hover:bg-surface-3 text-gray-400',
            )}
          >
            <span className="capitalize">{m.market}</span>
            <div className="flex items-center gap-3 font-mono">
              <span className="text-gray-600">
                {m.bets}b · {m.winRate}%W
              </span>
              <span className={m.roi >= 0 ? 'text-win' : 'text-loss'}>{m.roi.toFixed(1)}%</span>
              <span className={m.profit >= 0 ? 'text-win' : 'text-loss'}>
                {m.profit >= 0 ? '+' : ''}
                {formatCurrency(m.profit)}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Confidence chart ──────────────────────────

function ConfidenceChart({
  byConf,
  onFilter,
  activeConf,
}: {
  byConf: Record<string, { bets: number; profit: number; staked: number; wins: number }>;
  onFilter: (c: string | null) => void;
  activeConf: string | null;
}) {
  return (
    <div className="op-panel p-4">
      <div className="text-sm font-medium text-gray-300 mb-4">Performance by Confidence</div>
      <div className="space-y-3">
        {CONF_LEVELS.map((level) => {
          const c = byConf[level];
          if (!c) return null;
          const roi = c.staked > 0 ? (c.profit / c.staked) * 100 : 0;
          const winRate = c.bets > 0 ? (c.wins / c.bets) * 100 : 0;
          const isActive = activeConf === level;
          return (
            <button
              key={level}
              onClick={() => onFilter(isActive ? null : level)}
              className={clsx(
                'w-full text-left rounded p-2 transition-colors',
                isActive ? 'bg-brand/10 border border-brand/30' : 'hover:bg-surface-3',
              )}
            >
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span
                  className={clsx(
                    'font-medium capitalize',
                    level === 'high'
                      ? 'text-win'
                      : level === 'medium'
                        ? 'text-warn'
                        : 'text-gray-400',
                  )}
                >
                  {level}
                </span>
                <div className="flex items-center gap-3 font-mono text-gray-400">
                  <span>{c.bets} bets</span>
                  <span className={winRate >= 50 ? 'text-win' : 'text-loss'}>
                    {winRate.toFixed(0)}% W
                  </span>
                  <span className={roi >= 0 ? 'text-win' : 'text-loss'}>{roi.toFixed(1)}% ROI</span>
                  <span className={c.profit >= 0 ? 'text-win' : 'text-loss'}>
                    {c.profit >= 0 ? '+' : ''}
                    {formatCurrency(c.profit)}
                  </span>
                </div>
              </div>
              <div className="h-1.5 bg-surface-4 rounded-full overflow-hidden">
                <div
                  className={clsx('h-full rounded-full', roi >= 0 ? 'bg-win' : 'bg-loss')}
                  style={{ width: `${Math.min(100, Math.abs(roi) * 5)}%` }}
                />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Odds histogram ────────────────────────────

function OddsHistogram({ data }: { data: ReturnType<typeof computeStats>['oddsHist'] }) {
  return (
    <div className="op-panel p-4">
      <div className="text-sm font-medium text-gray-300 mb-4">Performance by Odds Range</div>
      {data.length === 0 ? (
        <div className="h-32 flex items-center justify-center text-xs text-gray-600">No data</div>
      ) : (
        <ResponsiveContainer width="100%" height={150}>
          <BarChart data={data} barSize={22}>
            <XAxis dataKey="label" tick={{ fontSize: 8, fill: '#6b7280' }} tickLine={false} />
            <YAxis tick={{ fontSize: 9, fill: '#6b7280' }} tickLine={false} axisLine={false} />
            <RechartTooltip
              {...TT}
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0]?.payload;
                return (
                  <div style={TT.contentStyle} className="rounded p-2 space-y-1">
                    <div style={TT.labelStyle}>{label}</div>
                    <div className="text-gray-300">{d.count} bets</div>
                    <div className={d.winRate >= 0.5 ? 'text-win' : 'text-loss'}>
                      Win rate: {(d.winRate * 100).toFixed(0)}%
                    </div>
                    <div className={d.roi >= 0 ? 'text-win' : 'text-loss'}>
                      ROI: {(d.roi * 100).toFixed(1)}%
                    </div>
                  </div>
                );
              }}
            />
            <ReferenceLine y={0} stroke="#374151" />
            <Bar dataKey="count" name="count">
              {data.map((e, i) => (
                <Cell key={i} fill={e.roi >= 0 ? '#10b981' : '#ef4444'} opacity={0.8} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
      <div className="mt-2 space-y-1">
        {data.map((d, i) => (
          <div key={i} className="flex items-center justify-between text-xs">
            <span className="text-gray-500 font-mono text-[10px] w-28">{d.label}</span>
            <div className="flex items-center gap-3 font-mono">
              <span className="text-gray-600">{d.count} bets</span>
              <span className={d.winRate >= 0.5 ? 'text-win' : 'text-loss'}>
                {(d.winRate * 100).toFixed(0)}% W
              </span>
              <span className={d.roi >= 0 ? 'text-win' : 'text-loss'}>
                {(d.roi * 100).toFixed(1)}% ROI
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Insights panel ────────────────────────────

function InsightsPanel({ bets }: { bets: Bet[] }) {
  const insights = useMemo(() => generateInsights(bets), [bets]);
  const iconMap = {
    positive: <TrendingUp className="w-3.5 h-3.5 text-win flex-shrink-0 mt-0.5" />,
    negative: <TrendingDown className="w-3.5 h-3.5 text-loss flex-shrink-0 mt-0.5" />,
    neutral: <Lightbulb className="w-3.5 h-3.5 text-brand flex-shrink-0 mt-0.5" />,
    warning: <AlertCircle className="w-3.5 h-3.5 text-warn flex-shrink-0 mt-0.5" />,
  };
  return (
    <div className="op-panel p-4">
      <div className="flex items-center gap-2 mb-3">
        <Lightbulb className="w-4 h-4 text-brand" />
        <span className="text-sm font-medium text-gray-300">Performance Insights</span>
        <span className="text-xs text-gray-600">
          — data-backed patterns from your filtered bets
        </span>
      </div>
      <div className="space-y-2">
        {insights.map((ins, i) => (
          <div key={i} className="flex items-start gap-2 text-xs text-gray-400">
            {iconMap[ins.type]}
            <span>{ins.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Bet table with drill-through ──────────────

type TableSortField =
  | 'gameDate'
  | 'odds'
  | 'stakeAmount'
  | 'pnl'
  | 'edge'
  | 'confidence'
  | 'dqScore'
  | 'clv';

function BetTable({ bets }: { bets: Bet[] }) {
  const settled = bets.filter((b) => ['won', 'lost', 'push', 'void'].includes(b.status));
  const [sort, setSort] = useState<{ field: TableSortField; dir: 'asc' | 'desc' }>({
    field: 'gameDate',
    dir: 'desc',
  });
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 15;

  const enriched = useMemo(
    () => settled.map((b) => ({ ...b, dqScore: scoreDecision(b) })),
    [settled],
  );

  const sorted = useMemo(() => {
    const mul = sort.dir === 'asc' ? 1 : -1;
    return [...enriched].sort((a, b) => {
      let cmp = 0;
      switch (sort.field) {
        case 'gameDate':
          cmp = a.gameDate.localeCompare(b.gameDate);
          break;
        case 'odds':
          cmp = a.odds - b.odds;
          break;
        case 'stakeAmount':
          cmp = a.stakeAmount - b.stakeAmount;
          break;
        case 'pnl':
          cmp = (a.pnl ?? 0) - (b.pnl ?? 0);
          break;
        case 'edge':
          cmp = (betEdge(a) ?? 0) - (betEdge(b) ?? 0);
          break;
        case 'confidence':
          cmp = (a.confidence ?? 0) - (b.confidence ?? 0);
          break;
        case 'dqScore':
          cmp = a.dqScore - b.dqScore;
          break;
        case 'clv':
          cmp = (a.clv ?? -Infinity) - (b.clv ?? -Infinity);
          break;
      }
      return cmp * mul;
    });
  }, [enriched, sort]);

  const pageData = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const SortTh = ({ field, children }: { field: TableSortField; children: React.ReactNode }) => (
    <th
      onClick={() => {
        setSort((s) =>
          s.field === field
            ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' }
            : { field, dir: 'desc' },
        );
        setPage(0);
      }}
      className="text-left text-[10px] text-gray-600 uppercase tracking-wide pb-2 cursor-pointer hover:text-gray-400 select-none whitespace-nowrap"
    >
      <span className="flex items-center gap-0.5">
        {children}
        {sort.field === field ? (
          sort.dir === 'asc' ? (
            <ArrowUp className="w-2.5 h-2.5" />
          ) : (
            <ArrowDown className="w-2.5 h-2.5" />
          )
        ) : (
          <ArrowUpDown className="w-2.5 h-2.5 opacity-30" />
        )}
      </span>
    </th>
  );

  if (settled.length === 0) return null;

  return (
    <div className="op-panel p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm font-medium text-gray-300">
          Bet Log{' '}
          <span className="text-gray-600 font-normal text-xs ml-1">
            ({settled.length} settled · click row to expand)
          </span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-surface-4">
              <SortTh field="gameDate">Date</SortTh>
              <th className="text-left text-[10px] text-gray-600 uppercase tracking-wide pb-2">
                Matchup
              </th>
              <th className="text-left text-[10px] text-gray-600 uppercase tracking-wide pb-2">
                Market
              </th>
              <th className="text-left text-[10px] text-gray-600 uppercase tracking-wide pb-2">
                Selection
              </th>
              <SortTh field="odds">Odds</SortTh>
              <SortTh field="stakeAmount">Stake</SortTh>
              <th className="text-left text-[10px] text-gray-600 uppercase tracking-wide pb-2">
                Result
              </th>
              <SortTh field="pnl">P&L</SortTh>
              <SortTh field="edge">Edge</SortTh>
              <SortTh field="clv">CLV</SortTh>
              <SortTh field="confidence">Conf</SortTh>
              <SortTh field="dqScore">DQ</SortTh>
            </tr>
          </thead>
          <tbody>
            {pageData.map((b) => {
              const isExp = expandedId === b.id;
              const { color: dqColor } = dqCategory(b.dqScore);
              const derivedEdge = betEdge(b);
              const ev = (derivedEdge ?? 0) * b.stakeAmount;
              const luck = (b.pnl ?? 0) - ev;
              return (
                <>
                  <tr
                    key={b.id}
                    onClick={() => setExpandedId(isExp ? null : b.id)}
                    className="border-b border-surface-4/50 hover:bg-surface-2 transition-colors cursor-pointer"
                  >
                    <td className="py-2 pr-3 font-mono text-gray-600">{b.gameDate}</td>
                    <td className="py-2 pr-3 text-gray-400 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <TeamLogo name={b.awayTeam} size="xs" />
                        {b.awayTeam.split(' ').pop()} @
                        <TeamLogo name={b.homeTeam} size="xs" />
                        {b.homeTeam.split(' ').pop()}
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-gray-500">{formatMarket(b.market)}</td>
                    <td className="py-2 pr-3 text-gray-200 font-medium max-w-[100px] truncate">
                      {b.selection}
                    </td>
                    <td
                      className={clsx(
                        'py-2 pr-3 font-mono',
                        b.odds > 0 ? 'text-win' : 'text-gray-300',
                      )}
                    >
                      {formatOdds(b.odds)}
                    </td>
                    <td className="py-2 pr-3 font-mono text-gray-400">
                      {formatCurrency(b.stakeAmount)}
                    </td>
                    <td className="py-2 pr-3">
                      <span
                        className={clsx('capitalize font-medium', getStatusColor(b.result ?? ''))}
                      >
                        {b.result ?? '—'}
                      </span>
                    </td>
                    <td
                      className={clsx(
                        'py-2 pr-3 font-mono font-semibold',
                        (b.pnl ?? 0) >= 0 ? 'text-win' : 'text-loss',
                      )}
                    >
                      {b.pnl !== undefined ? formatPnl(b.pnl) : '—'}
                    </td>
                    <td className="py-2 pr-3 font-mono text-gray-500">
                      {derivedEdge !== undefined ? (
                        <span
                          title={
                            b.edge === undefined ? 'Derived from model/implied prob' : undefined
                          }
                          className={b.edge === undefined ? 'text-gray-600' : ''}
                        >
                          {`${(derivedEdge * 100).toFixed(1)}%`}
                          {b.edge === undefined && (
                            <span className="text-gray-700 text-[9px] ml-0.5">~</span>
                          )}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-2 pr-3 font-mono">
                      {b.clv != null ? (
                        <span className={b.clv >= 0 ? 'text-win' : 'text-loss'}>
                          {b.clv >= 0 ? '+' : ''}
                          {(b.clv * 100).toFixed(2)}pp
                        </span>
                      ) : (
                        <span className="text-gray-700">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 font-mono text-gray-500">
                      {b.confidence !== undefined ? (
                        `${(b.confidence * 100).toFixed(0)}%`
                      ) : (
                        <span title="Inferred from edge" className="text-gray-600">
                          {betConfTier(b)}
                        </span>
                      )}
                    </td>
                    <td className={clsx('py-2 font-mono font-semibold text-[11px]', dqColor)}>
                      {b.dqScore}
                    </td>
                  </tr>
                  {isExp && (
                    <tr key={`${b.id}-exp`} className="border-b border-surface-4/50 bg-surface-2">
                      <td colSpan={12} className="px-3 py-3">
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                          <div className="space-y-1.5">
                            <div className="text-[10px] uppercase tracking-wide text-gray-600 mb-1">
                              Probabilities
                            </div>
                            <Row2 label="Implied prob" val={formatPct(b.impliedProbability)} />
                            {b.modelProbability !== undefined && (
                              <Row2 label="Model prob" val={formatPct(b.modelProbability)} accent />
                            )}
                            {derivedEdge !== undefined && (
                              <Row2
                                label={b.edge !== undefined ? 'Edge' : 'Edge (derived)'}
                                val={`+${(derivedEdge * 100).toFixed(2)}pp`}
                                accent
                              />
                            )}
                            {b.clv != null && (
                              <Row2
                                label="CLV"
                                val={`${b.clv >= 0 ? '+' : ''}${(b.clv * 100).toFixed(2)}pp`}
                                accent={b.clv >= 0}
                                negative={b.clv < 0}
                              />
                            )}
                            {b.closingOdds != null && (
                              <Row2
                                label="Closing odds"
                                val={`${b.closingOdds > 0 ? '+' : ''}${b.closingOdds}`}
                              />
                            )}
                          </div>
                          <div className="space-y-1.5">
                            <div className="text-[10px] uppercase tracking-wide text-gray-600 mb-1">
                              Expected vs Actual
                            </div>
                            <Row2
                              label="Expected P&L (EV)"
                              val={`${ev >= 0 ? '+' : ''}${formatCurrency(Math.abs(ev))}`}
                              accent={ev >= 0}
                            />
                            <Row2
                              label="Actual P&L"
                              val={b.pnl !== undefined ? formatPnl(b.pnl) : '—'}
                              accent={(b.pnl ?? 0) >= 0}
                              negative={(b.pnl ?? 0) < 0}
                            />
                            {derivedEdge !== undefined && (
                              <Row2
                                label="Luck factor"
                                val={`${luck >= 0 ? '+' : ''}${formatCurrency(Math.abs(luck))}`}
                                accent={luck >= 0}
                                negative={luck < 0}
                              />
                            )}
                          </div>
                          <div className="space-y-1.5">
                            <div className="text-[10px] uppercase tracking-wide text-gray-600 mb-1">
                              Decision Quality
                            </div>
                            <Row2 label="DQ Score" val={`${b.dqScore}/100`} />
                            <Row2 label="Category" val={dqCategory(b.dqScore).label} />
                            <Row2 label="Model rec" val={b.recommendationId ? 'Yes' : 'Manual'} />
                          </div>
                          <div className="space-y-1.5">
                            <div className="text-[10px] uppercase tracking-wide text-gray-600 mb-1">
                              Stake
                            </div>
                            <Row2 label="Amount" val={formatCurrency(b.stakeAmount)} />
                            <Row2
                              label="Potential return"
                              val={formatCurrency(b.potentialReturn)}
                            />
                            {b.settledAt && (
                              <Row2
                                label="Settled"
                                val={new Date(b.settledAt).toLocaleDateString('en-US', {
                                  timeZone: 'America/Los_Angeles',
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })}
                              />
                            )}
                          </div>
                        </div>
                        {b.notes && (
                          <div className="mt-2 text-xs text-gray-500 italic">{b.notes}</div>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
      <PaginationControls
        totalItems={sorted.length}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />
    </div>
  );
}

// ── Model Calibration ─────────────────────────

const AGENT_LABELS: Record<string, string> = {
  market: 'Market',
  matchup: 'Matchup',
  form: 'Form',
  context: 'Context',
  risk: 'Risk',
  validation: 'Validation',
  portfolio: 'Portfolio',
  closingLineValue: 'Closing Line Value',
};

function CalibrationPanel({ calibration }: { calibration: CalibrationMetrics | null }) {
  if (!calibration) return null;

  if (calibration.sampleCount < 5) {
    return (
      <div className="op-panel p-4">
        <div className="text-sm font-medium text-gray-300 mb-1">Model Calibration</div>
        <div className="h-20 flex items-center justify-center text-xs text-gray-600">
          Not enough settled bets yet — need at least 5 won/lost outcomes to compute calibration
        </div>
      </div>
    );
  }

  const chartData = calibration.bins.map((bin) => ({
    label: `${Math.round(bin.minConfidence * 100)}–${Math.round(bin.maxConfidence * 100)}%`,
    actual: bin.count > 0 && !bin.lowSample ? bin.winRate : null,
    perfect: (bin.minConfidence + bin.maxConfidence) / 2,
    count: bin.count,
    lowSample: bin.lowSample,
  }));

  const brierLabel =
    calibration.brierScore < 0.2
      ? 'Excellent'
      : calibration.brierScore < 0.23
        ? 'Good'
        : calibration.brierScore < 0.25
          ? 'Fair'
          : 'Below random';

  const biasColor =
    calibration.bias === 'well_calibrated'
      ? 'text-win'
      : calibration.bias === 'overconfident'
        ? 'text-loss'
        : 'text-warn';

  const biasLabel =
    calibration.bias === 'well_calibrated'
      ? 'Well calibrated'
      : calibration.bias === 'overconfident'
        ? 'Overconfident'
        : 'Underconfident';

  return (
    <div className="op-panel p-4 space-y-4">
      <div className="text-sm font-medium text-gray-300">Model Calibration</div>

      {/* Uncalibrated weights warning */}
      {!calibration.weightsCalibrated && (
        <div className="flex items-start gap-2 rounded border border-warn/40 bg-warn/10 px-3 py-2 text-xs text-warn">
          <span className="mt-0.5 shrink-0">!</span>
          <span>
            <span className="font-semibold">Weights uncalibrated.</span> Agent adjustment constants
            are manually tuned defaults. Accumulate ~300 settled bets, run{' '}
            <span className="font-mono">npm run calibrate</span>, review{' '}
            <span className="font-mono">data/candidate_weights.json</span>, then copy to{' '}
            <span className="font-mono">data/active_weights.json</span> and restart the server.
          </span>
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-surface-3 rounded p-3 text-center">
          <div className="text-xs text-gray-500 mb-1">Brier Score</div>
          <div className="font-mono text-lg font-semibold text-gray-100">
            {calibration.brierScore.toFixed(3)}
          </div>
          <div className="text-[10px] text-gray-500 mt-0.5">{brierLabel} · lower is better</div>
        </div>
        <div className="bg-surface-3 rounded p-3 text-center">
          <div className="text-xs text-gray-500 mb-1">Calibration Error</div>
          <div className="font-mono text-lg font-semibold text-gray-100">
            {(calibration.ece * 100).toFixed(1)}%
          </div>
          <div className="text-[10px] text-gray-500 mt-0.5">Expected avg deviation</div>
        </div>
        <div className="bg-surface-3 rounded p-3 text-center">
          <div className="text-xs text-gray-500 mb-1">Bias</div>
          <div className={clsx('font-semibold text-base', biasColor)}>{biasLabel}</div>
          <div className="text-[10px] text-gray-500 mt-0.5">
            Pred {(calibration.avgConfidence * 100).toFixed(0)}% · Actual{' '}
            {(calibration.avgWinRate * 100).toFixed(0)}% win
          </div>
        </div>
      </div>

      {/* Calibration plot */}
      <div>
        <div className="text-xs text-gray-500 mb-2">Predicted Confidence vs Actual Win Rate</div>
        <ResponsiveContainer width="100%" height={160}>
          <LineChart data={chartData} margin={{ top: 4, right: 4, bottom: 4, left: -10 }}>
            <XAxis dataKey="label" tick={{ fontSize: 8, fill: '#6b7280' }} tickLine={false} />
            <YAxis
              tick={{ fontSize: 8, fill: '#6b7280' }}
              tickLine={false}
              axisLine={false}
              domain={[0, 1]}
              tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`}
            />
            <RechartTooltip
              {...TT}
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0]?.payload;
                return (
                  <div style={TT.contentStyle} className="rounded p-2 space-y-1">
                    <div style={TT.labelStyle}>Confidence {label}</div>
                    <div className="text-gray-300">{d.count} bets</div>
                    {d.actual != null ? (
                      <div
                        className={
                          Math.abs(d.actual - d.perfect) < 0.05
                            ? 'text-win'
                            : Math.abs(d.actual - d.perfect) < 0.15
                              ? 'text-warn'
                              : 'text-loss'
                        }
                      >
                        Actual win rate: {(d.actual * 100).toFixed(0)}%
                      </div>
                    ) : (
                      <div className="text-gray-600">Too few bets (&lt;5)</div>
                    )}
                    <div className="text-gray-500">Perfect: {(d.perfect * 100).toFixed(0)}%</div>
                  </div>
                );
              }}
            />
            {/* Perfect calibration diagonal */}
            <Line
              type="monotone"
              dataKey="perfect"
              stroke="#374151"
              strokeDasharray="4 2"
              dot={false}
              strokeWidth={1}
              name="Perfect"
            />
            {/* Actual calibration */}
            <Line
              type="monotone"
              dataKey="actual"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={(props) => {
                const { cx, cy, payload } = props as {
                  cx: number;
                  cy: number;
                  payload: { actual: number | null; perfect: number; lowSample: boolean };
                };
                if (payload.actual == null) return <g key={`dot-${cx}`} />;
                const diff = Math.abs(payload.actual - payload.perfect);
                const color = diff < 0.05 ? '#10b981' : diff < 0.15 ? '#f59e0b' : '#ef4444';
                return (
                  <circle
                    key={`dot-${cx}`}
                    cx={cx}
                    cy={cy}
                    r={4}
                    fill={color}
                    stroke="transparent"
                  />
                );
              }}
              connectNulls={false}
              name="Actual"
            />
            <Legend iconSize={8} wrapperStyle={{ fontSize: 9, color: '#6b7280' }} />
          </LineChart>
        </ResponsiveContainer>
        <div className="flex items-center gap-4 text-[10px] text-gray-600 mt-1">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-win inline-block" /> Within 5% (good)
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-warn inline-block" /> 5–15% off
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-loss inline-block" /> &gt;15% off
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-gray-700 inline-block" /> &lt;5 bets (excluded)
          </span>
        </div>
      </div>

      {/* Agent table */}
      <div>
        <div className="text-xs text-gray-500 mb-2">Agent Performance — ranked by Brier Score</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-600 border-b border-surface-4">
                <th className="text-left pb-1.5 font-normal">Agent</th>
                <th className="text-right pb-1.5 font-normal">Brier Score</th>
                <th className="text-right pb-1.5 font-normal">Accuracy</th>
                <th className="text-right pb-1.5 font-normal">Avg Score</th>
                <th className="text-right pb-1.5 font-normal">vs Baseline</th>
                <th className="text-right pb-1.5 font-normal">Verdict</th>
              </tr>
            </thead>
            <tbody>
              {calibration.agents.map((agent) => {
                const verdictColor =
                  agent.verdict === 'adding_value'
                    ? 'text-win'
                    : agent.verdict === 'neutral'
                      ? 'text-gray-400'
                      : 'text-loss';
                const verdictLabel =
                  agent.verdict === 'adding_value'
                    ? 'Adding value'
                    : agent.verdict === 'neutral'
                      ? 'Neutral'
                      : 'May be noise';
                const baselineColor =
                  agent.vsBaseline > 0.02
                    ? 'text-win'
                    : agent.vsBaseline > -0.01
                      ? 'text-gray-400'
                      : 'text-loss';
                return (
                  <tr
                    key={agent.agentName}
                    className="border-b border-surface-4/50 hover:bg-surface-3/30"
                  >
                    <td className="py-1.5 font-medium text-gray-200">
                      {AGENT_LABELS[agent.agentName] ?? agent.agentName}
                    </td>
                    <td className="py-1.5 text-right font-mono text-gray-300">
                      {agent.sampleCount === 0 ? '—' : agent.brierScore.toFixed(3)}
                    </td>
                    <td className="py-1.5 text-right font-mono text-gray-300">
                      {agent.sampleCount === 0 ? '—' : `${(agent.accuracy * 100).toFixed(0)}%`}
                    </td>
                    <td className="py-1.5 text-right font-mono text-gray-300">
                      {agent.sampleCount === 0 ? '—' : agent.avgScore.toFixed(2)}
                    </td>
                    <td className={clsx('py-1.5 text-right font-mono', baselineColor)}>
                      {agent.sampleCount === 0
                        ? '—'
                        : `${agent.vsBaseline > 0 ? '+' : ''}${agent.vsBaseline.toFixed(3)}`}
                    </td>
                    <td className={clsx('py-1.5 text-right', verdictColor)}>
                      {agent.sampleCount === 0 ? (
                        <span className="text-gray-600">No data</span>
                      ) : (
                        verdictLabel
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="text-[10px] text-gray-600 mt-2">
            Brier Score: 0.25 = random · lower is better · vs Baseline = improvement over always
            predicting 50%
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Model Intelligence Panel ─────────────────

interface StatsShape {
  settled: number;
  wins: number;
  roi: number;
  byConf: Record<string, { bets: number; profit: number; staked: number; wins: number }>;
}

function ModelIntelligencePanel({ stats, allStats }: { stats: StatsShape; allStats: StatsShape }) {
  const highConf = allStats.byConf['high'];
  const medConf = allStats.byConf['medium'];

  if (!highConf || highConf.bets < 3) return null;

  const highWinRate = highConf.wins / highConf.bets;
  const overallWinRate = allStats.settled > 0 ? allStats.wins / allStats.settled : 0;
  const highROI = highConf.staked > 0 ? highConf.profit / highConf.staked : 0;
  const winRateDiff = highWinRate - overallWinRate;
  const roiDiff = highROI - allStats.roi;

  const rows: { label: string; high: string; overall: string; diffCls: string; diff: string }[] = [
    {
      label: 'Win Rate',
      high: `${(highWinRate * 100).toFixed(1)}%`,
      overall: `${(overallWinRate * 100).toFixed(1)}%`,
      diff:
        winRateDiff >= 0
          ? `+${(winRateDiff * 100).toFixed(1)}%`
          : `${(winRateDiff * 100).toFixed(1)}%`,
      diffCls:
        winRateDiff > 0.02 ? 'text-win' : winRateDiff > -0.02 ? 'text-gray-400' : 'text-loss',
    },
    {
      label: 'ROI',
      high: `${(highROI * 100).toFixed(1)}%`,
      overall: `${(allStats.roi * 100).toFixed(1)}%`,
      diff: roiDiff >= 0 ? `+${(roiDiff * 100).toFixed(1)}%` : `${(roiDiff * 100).toFixed(1)}%`,
      diffCls: roiDiff > 0.02 ? 'text-win' : roiDiff > -0.02 ? 'text-gray-400' : 'text-loss',
    },
  ];

  const behaviorInsight =
    winRateDiff > 0.05
      ? `Following high-confidence calls adds ${(winRateDiff * 100).toFixed(0)}pp win rate — the model's top picks are outperforming.`
      : winRateDiff > 0
        ? 'High-confidence calls modestly outperform average. The model shows some selection skill at the top tier.'
        : 'High-confidence calls are not outperforming yet. Consider reviewing model calibration.';

  return (
    <div className="op-panel p-4">
      <div className="flex items-center gap-2 mb-3">
        <Target className="w-4 h-4 text-brand" />
        <span className="text-sm font-semibold text-gray-200">Model Intelligence</span>
        <span className="text-xs text-gray-600 ml-auto">
          {highConf.bets} high-conf bets analyzed
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3">
        <div>
          <div className="text-xs text-gray-500 mb-1">High Confidence vs Overall</div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-600 border-b border-surface-4">
                <th className="text-left pb-1 font-normal">Metric</th>
                <th className="text-right pb-1 font-normal">High Conf</th>
                <th className="text-right pb-1 font-normal">Overall</th>
                <th className="text-right pb-1 font-normal">Delta</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.label} className="border-b border-surface-4/50">
                  <td className="py-1 text-gray-400">{r.label}</td>
                  <td className="py-1 text-right font-mono text-gray-200">{r.high}</td>
                  <td className="py-1 text-right font-mono text-gray-500">{r.overall}</td>
                  <td className={clsx('py-1 text-right font-mono font-semibold', r.diffCls)}>
                    {r.diff}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {medConf && medConf.bets >= 2 && (
          <div>
            <div className="text-xs text-gray-500 mb-1">By Confidence Tier</div>
            <div className="space-y-2">
              {(['high', 'medium', 'low'] as const).map((tier) => {
                const d = allStats.byConf[tier];
                if (!d || d.bets === 0) return null;
                const wr = d.wins / d.bets;
                const cls =
                  tier === 'high' ? 'bg-win' : tier === 'medium' ? 'bg-warn' : 'bg-gray-600';
                return (
                  <div key={tier}>
                    <div className="flex justify-between text-xs text-gray-500 mb-0.5">
                      <span className="capitalize">{tier}</span>
                      <span className="font-mono">
                        {(wr * 100).toFixed(0)}% ({d.bets})
                      </span>
                    </div>
                    <div className="h-1.5 bg-surface-3 rounded-full overflow-hidden">
                      <div
                        className={clsx('h-full rounded-full', cls)}
                        style={{ width: `${wr * 100}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div>
          <div className="text-xs text-gray-500 mb-1">Behavioral Insight</div>
          <p className="text-xs text-gray-400 leading-relaxed">{behaviorInsight}</p>
          {highConf.bets < 10 && (
            <p className="text-xs text-gray-600 mt-2">
              Based on {highConf.bets} high-confidence bets — accuracy improves with more data.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Calibration Health Panel ──────────────────

function CalibrationHealthPanel({ metrics }: { metrics: CalibrationRunMetrics | null }) {
  if (!metrics || !metrics.factorPValues?.length) return null;
  return (
    <div className="op-panel p-4">
      <div className="text-sm font-medium text-gray-300 mb-3">Calibration Health</div>
      <div className="grid grid-cols-3 gap-3 mb-4 text-center text-xs">
        <div className="bg-surface-3 rounded p-2">
          <div className="text-gray-500 mb-0.5">Candidate Brier</div>
          <div className="font-mono font-semibold text-gray-100">
            {metrics.brierScoreCandidate.toFixed(4)}
          </div>
          {metrics.brierCiLower != null && metrics.brierCiUpper != null && (
            <div className="text-[10px] text-gray-600 mt-0.5">
              95% CI [{metrics.brierCiLower.toFixed(4)}, {metrics.brierCiUpper.toFixed(4)}]
            </div>
          )}
        </div>
        <div className="bg-surface-3 rounded p-2">
          <div className="text-gray-500 mb-0.5">Null Brier</div>
          <div className="font-mono font-semibold text-gray-100">
            {metrics.brierScoreNull.toFixed(4)}
          </div>
          <div className="text-[10px] text-gray-600 mt-0.5">predict win rate always</div>
        </div>
        <div className="bg-surface-3 rounded p-2">
          <div className="text-gray-500 mb-0.5">Verdict</div>
          <div className={clsx('font-semibold', metrics.shouldPromote ? 'text-win' : 'text-loss')}>
            {metrics.shouldPromote ? 'Promote' : 'Discard'}
          </div>
          <div className="text-[10px] text-gray-600 mt-0.5">
            {metrics.holdOutCount} hold-out bets
          </div>
        </div>
      </div>
      <div className="text-xs text-gray-500 mb-2">Factor p-values (α = 0.05)</div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-gray-500 border-b border-surface-4">
            <th className="text-left pb-1.5">Factor</th>
            <th className="text-right pb-1.5">n</th>
            <th className="text-right pb-1.5">slope</th>
            <th className="text-right pb-1.5">p-value</th>
            <th className="text-right pb-1.5">sig?</th>
          </tr>
        </thead>
        <tbody>
          {metrics.factorPValues.map((f) => (
            <tr key={f.factor} className="border-b border-surface-4/50">
              <td className="py-1 text-gray-300 font-mono">{f.factor}</td>
              <td className="py-1 text-right text-gray-500">{f.n}</td>
              <td className="py-1 text-right text-gray-400">{f.slope.toFixed(4)}</td>
              <td className="py-1 text-right text-gray-300">{f.pValue.toFixed(4)}</td>
              <td className={clsx('py-1 text-right font-medium', f.isSignificant ? 'text-win' : 'text-warn')}>
                {f.isSignificant ? '✓' : 'ns'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[10px] text-gray-600 mt-2">
        Last run: {new Date(metrics.runAt).toLocaleString()} · ns = not significant
      </p>
    </div>
  );
}

// ── Weight Tuning ─────────────────────────────

function WeightTuningPanel() {
  const [calibStatus, setCalibStatus] = useState<CalibrationStatus | null>(null);
  const [calibRunning, setCalibRunning] = useState(false);
  const [calibOutput, setCalibOutput] = useState<string | null>(null);
  const [candidate, setCandidate] = useState<CandidateWeightComparison | null>(null);
  const [candidateExpanded, setCandidateExpanded] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [promoteConfirm, setPromoteConfirm] = useState(false);

  useEffect(() => {
    calibrationApi.getStatus().then(setCalibStatus).catch(() => setCalibStatus(null));
  }, []);

  const runCalibration = async () => {
    setCalibRunning(true);
    setCalibOutput(null);
    try {
      const result = await calibrationApi.run();
      setCalibOutput(result.output);
      if (result.candidateWritten) {
        const [status, cand] = await Promise.all([
          calibrationApi.getStatus(),
          calibrationApi.getCandidate(),
        ]);
        setCalibStatus(status);
        setCandidate(cand);
        setCandidateExpanded(true);
      } else {
        setCalibStatus(await calibrationApi.getStatus());
      }
    } finally {
      setCalibRunning(false);
    }
  };

  const loadCandidate = async () => {
    try {
      const cand = await calibrationApi.getCandidate();
      setCandidate(cand);
      setCandidateExpanded(true);
    } catch {
      setCandidate(null);
    }
  };

  const promoteWeights = async () => {
    setPromoting(true);
    try {
      await calibrationApi.promote();
      await calibrationApi.discard();
      setCalibStatus(await calibrationApi.getStatus());
      setCandidate(null);
      setPromoteConfirm(false);
    } finally {
      setPromoting(false);
    }
  };

  const discardCandidate = async () => {
    setDiscarding(true);
    try {
      await calibrationApi.discard();
      setCalibStatus(await calibrationApi.getStatus());
      setCandidate(null);
      setCandidateExpanded(false);
    } finally {
      setDiscarding(false);
    }
  };

  return (
    <div className="op-panel p-4">
      <div className="flex items-center gap-2 mb-3">
        <Brain className="w-4 h-4 text-brand" />
        <div className="text-sm font-medium text-gray-300">Weight Tuning</div>
        {calibStatus && (
          <span
            className={clsx(
              'ml-auto text-xs px-2 py-0.5 rounded border',
              calibStatus.usingCustomWeights ? 'confidence-high' : 'confidence-low',
            )}
          >
            {calibStatus.usingCustomWeights ? 'custom weights' : 'default weights'}
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Run logistic regression over settled bet outcomes to tune per-factor weights. Needs 50+
        samples per factor; 300+ for full calibration.
      </p>

      {calibStatus && (
        <div className="mb-3 space-y-1.5">
          {['matchup', 'form', 'context'].map((group) => {
            const factors = calibStatus.sampleCounts.filter((f) => f.factorGroup === group);
            const total = factors.reduce((s, f) => s + f.count, 0);
            const maxPossible = factors.length * 300;
            const pct =
              maxPossible > 0 ? Math.min(100, Math.round((total / maxPossible) * 100)) : 0;
            const readyCount = factors.filter((f) => f.isReady).length;
            return (
              <div key={group}>
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-xs text-gray-400 capitalize">{group}</span>
                  <span className="text-xs text-gray-500">
                    {readyCount}/{factors.length} factors ready
                  </span>
                </div>
                <div className="h-1.5 bg-surface-3 rounded-full overflow-hidden">
                  <div
                    className={clsx(
                      'h-full rounded-full transition-all',
                      readyCount === factors.length ? 'bg-win' : 'bg-brand',
                    )}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          onClick={runCalibration}
          disabled={calibRunning}
          className="btn-primary text-xs flex items-center gap-1.5"
        >
          <Play className="w-3 h-3" />
          {calibRunning ? 'Running…' : 'Run Calibration'}
        </button>
        {calibStatus?.candidateExists && !candidate && (
          <button onClick={loadCandidate} className="btn-secondary text-xs">
            Review Candidate
          </button>
        )}
      </div>

      {calibOutput && (
        <div className="mt-3 bg-surface-3 rounded p-2 max-h-32 overflow-y-auto">
          <pre className="text-xs text-gray-400 whitespace-pre-wrap font-mono">{calibOutput}</pre>
        </div>
      )}

      {calibStatus?.candidateExists && (
        <div className="mt-3 border border-surface-4 rounded">
          <button
            onClick={() => {
              setCandidateExpanded((p) => !p);
              if (!candidate) loadCandidate();
            }}
            className="w-full flex items-center justify-between px-3 py-2 text-xs text-gray-300 hover:bg-surface-3"
          >
            <span className="flex items-center gap-1.5">
              <ArrowUpCircle className="w-3.5 h-3.5 text-win" />
              Candidate weights ready
            </span>
            {candidateExpanded ? (
              <ChevronUp className="w-3.5 h-3.5" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5" />
            )}
          </button>

          {candidateExpanded && candidate && (
            <div className="px-3 pb-3">
              <table className="w-full text-xs mt-1">
                <thead>
                  <tr className="text-gray-500 border-b border-surface-4">
                    <th className="text-left pb-1 font-normal">Factor</th>
                    <th className="text-right pb-1 font-normal">Current</th>
                    <th className="text-right pb-1 font-normal">Candidate</th>
                    <th className="text-right pb-1 font-normal">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {candidate.diffs
                    .filter((d) => d.pctChange !== 0)
                    .sort((a, b) => Math.abs(b.pctChange) - Math.abs(a.pctChange))
                    .slice(0, 20)
                    .map((d) => (
                      <tr key={d.path} className="border-b border-surface-4/50">
                        <td className="py-1 text-gray-400 font-mono truncate max-w-[140px]">
                          {d.path}
                        </td>
                        <td className="py-1 text-right text-gray-400">{d.current.toFixed(3)}</td>
                        <td className="py-1 text-right text-gray-300">{d.candidate.toFixed(3)}</td>
                        <td
                          className={clsx(
                            'py-1 text-right font-medium',
                            d.pctChange > 0 ? 'text-win' : 'text-loss',
                          )}
                        >
                          {d.pctChange > 0 ? '+' : ''}
                          {d.pctChange}%
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>

              {promoteConfirm ? (
                <div className="mt-3 p-2 bg-surface-3 rounded text-xs text-gray-300">
                  <p className="mb-2">
                    Promote these weights? Next analysis run will use the new values.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={promoteWeights}
                      disabled={promoting}
                      className="btn-primary text-xs"
                    >
                      {promoting ? 'Promoting…' : 'Confirm Promote'}
                    </button>
                    <button
                      onClick={() => setPromoteConfirm(false)}
                      className="btn-secondary text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => setPromoteConfirm(true)}
                    className="btn-primary text-xs flex items-center gap-1"
                  >
                    <ArrowUpCircle className="w-3 h-3" />
                    Promote to Active
                  </button>
                  <button
                    onClick={discardCandidate}
                    disabled={discarding}
                    className="btn-danger text-xs flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" />
                    {discarding ? 'Discarding…' : 'Discard'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────

export function AnalyticsPage({ platformTitle }: { platformTitle?: string } = {}) {
  const { bets, fetchBets, bankrollSettings } = useAppStore();
  const [history, setHistory] = useState<BankrollSnapshot[]>([]);
  const [calibration, setCalibration] = useState<CalibrationMetrics | null>(null);
  const [runMetrics, setRunMetrics] = useState<CalibrationRunMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [activeMarket, setActiveMarket] = useState<string | null>(null);
  const [activeConf, setActiveConf] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([
      fetchBets(),
      bankrollApi.getHistory().then(setHistory),
      bankrollApi
        .getCalibration()
        .then(setCalibration)
        .catch(() => null),
      calibrationApi
        .getMetrics()
        .then((m) => setRunMetrics(m ?? null))
        .catch(() => null),
    ]);
    setLoading(false);
  };
  useEffect(() => {
    fetchData();
  }, []);

  // Re-fetch bankroll history silently when bets update (SSE bets_updated event)
  useEffect(() => {
    bankrollApi.getHistory().then(setHistory);
    bankrollApi
      .getCalibration()
      .then(setCalibration)
      .catch(() => null);
  }, [bets]);

  const effectiveFilters = useMemo(
    (): Filters => ({
      ...filters,
      markets: activeMarket
        ? [...new Set([...filters.markets, activeMarket as BetMarket])]
        : filters.markets,
      confidence: activeConf
        ? [...new Set([...filters.confidence, activeConf])]
        : filters.confidence,
    }),
    [filters, activeMarket, activeConf],
  );

  const filtered = useMemo(() => applyFilters(bets, effectiveFilters), [bets, effectiveFilters]);
  const stats = useMemo(() => computeStats(filtered), [filtered]);
  const allStats = useMemo(() => computeStats(bets), [bets]);
  const isFiltered =
    countActiveFilters(effectiveFilters) > 0 || activeMarket !== null || activeConf !== null;
  const startBal = bankrollSettings?.startingBankroll ?? 100;

  const handleMarketFilter = (m: string | null) => {
    setActiveMarket(m);
    if (m) setFilters((f) => ({ ...f, markets: [] }));
  };
  const handleConfFilter = (c: string | null) => {
    setActiveConf(c);
    if (c) setFilters((f) => ({ ...f, confidence: [] }));
  };
  const resetAll = () => {
    setFilters(DEFAULT_FILTERS);
    setActiveMarket(null);
    setActiveConf(null);
  };

  const histChart = history.map((s, i, arr) => ({
    date: s.date,
    balance: s.balance,
    prevBalance: i > 0 ? arr[i - 1].balance : null,
  }));

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-center justify-between h-10 px-4 border-b border-surface-4 bg-surface-1 flex-shrink-0">
        <h1 className="text-xs font-semibold uppercase tracking-wider text-gray-400">{platformTitle ?? 'Analytics'}</h1>
      </div>
      <DiamondEdgeSubNav />

      {loading ? (
        <LoadingScreen message="Computing analytics..." />
      ) : (
        <div className="flex-1 overflow-y-auto">
          {/* Filter bar */}
          <div className="sticky top-0 z-10 bg-surface-0 border-b border-surface-4 px-6 py-3 flex items-center gap-3 flex-wrap">
            <FilterBar
              filters={filters}
              onChange={(f) => {
                setFilters(f);
                setActiveMarket(null);
                setActiveConf(null);
              }}
            />
            {isFiltered && (
              <button
                onClick={resetAll}
                className="text-xs text-gray-600 hover:text-gray-400 ml-auto"
              >
                Clear all filters
              </button>
            )}
          </div>

          <div className="p-4 space-y-4">
            {/* KPI row */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
              <KPICard
                label="Net Profit"
                value={`${stats.netProfit >= 0 ? '+' : ''}${formatCurrency(stats.netProfit)}`}
                sub={
                  isFiltered
                    ? `All-time: ${allStats.netProfit >= 0 ? '+' : ''}${formatCurrency(allStats.netProfit)}`
                    : undefined
                }
                accent={stats.netProfit >= 0}
                negative={stats.netProfit < 0}
                tooltip="Total profit/loss after all settled bets"
              />
              <KPICard
                label="ROI"
                value={formatROI(stats.roi)}
                sub={isFiltered ? `All-time: ${formatROI(allStats.roi)}` : undefined}
                accent={stats.roi >= 0}
                negative={stats.roi < 0}
                tooltip="Return on investment: net profit ÷ total staked"
              />
              <KPICard
                label="Win Rate"
                value={formatPct(stats.winRate)}
                sub={`${stats.wins}W · ${stats.losses}L · ${stats.pushes}P`}
                tooltip="Percentage of settled bets that won"
              />
              <KPICard
                label="Settled"
                value={String(stats.settled)}
                sub={
                  stats.total > stats.settled
                    ? `${stats.total - stats.settled} open`
                    : 'all settled'
                }
                tooltip="Number of bets with a final outcome"
              />
              <KPICard
                label="Avg Edge"
                value={formatPct(stats.avgEdge)}
                accent={stats.avgEdge > 0}
                tooltip="Average model edge: model prob − implied prob"
              />
              <KPICard
                label="Avg CLV"
                value={
                  stats.avgCLV != null
                    ? `${stats.avgCLV >= 0 ? '+' : ''}${(stats.avgCLV * 100).toFixed(2)}pp`
                    : '—'
                }
                sub={
                  stats.clvSampleSize > 0
                    ? `${stats.clvSampleSize} bet${stats.clvSampleSize !== 1 ? 's' : ''} tracked`
                    : 'add closing odds to track'
                }
                accent={stats.avgCLV != null && stats.avgCLV > 0}
                negative={stats.avgCLV != null && stats.avgCLV < 0}
                tooltip="Closing Line Value: avg difference between closing implied prob and your entry implied prob. Positive = you consistently beat the market's final price — the gold standard for verified edge."
              />
              <KPICard
                label="Avg Stake"
                value={formatCurrency(stats.avgStake)}
                sub={`Avg odds: ${stats.avgOdds > 0 ? '+' : ''}${stats.avgOdds.toFixed(0)}`}
                tooltip="Average amount staked per settled bet"
              />
            </div>

            {/* Results bar + Bankroll chart */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-2 op-panel p-4">
                <div className="text-sm font-medium text-gray-300 mb-4">Results Breakdown</div>
                {stats.settled === 0 ? (
                  <div className="h-20 flex items-center justify-center text-xs text-gray-600">
                    No settled bets
                  </div>
                ) : (
                  <>
                    <div className="h-4 rounded-full overflow-hidden bg-surface-4 flex mb-3">
                      {stats.wins > 0 && (
                        <div
                          title={`Won: ${stats.wins}`}
                          className="bg-win h-full cursor-pointer hover:opacity-80"
                          style={{ width: `${(stats.wins / stats.settled) * 100}%` }}
                          onClick={() => setFilters((f) => ({ ...f, results: ['won'] }))}
                        />
                      )}
                      {stats.losses > 0 && (
                        <div
                          title={`Lost: ${stats.losses}`}
                          className="bg-loss h-full cursor-pointer hover:opacity-80"
                          style={{ width: `${(stats.losses / stats.settled) * 100}%` }}
                          onClick={() => setFilters((f) => ({ ...f, results: ['lost'] }))}
                        />
                      )}
                      {stats.pushes > 0 && (
                        <div
                          className="bg-gray-500 h-full"
                          style={{ width: `${(stats.pushes / stats.settled) * 100}%` }}
                        />
                      )}
                    </div>
                    <div className="flex items-center justify-between text-xs font-mono">
                      <span
                        className="text-win cursor-pointer hover:underline"
                        onClick={() => setFilters((f) => ({ ...f, results: ['won'] }))}
                      >
                        {stats.wins} Won ({((stats.wins / stats.settled) * 100).toFixed(0)}%)
                      </span>
                      <span
                        className="text-loss cursor-pointer hover:underline"
                        onClick={() => setFilters((f) => ({ ...f, results: ['lost'] }))}
                      >
                        {stats.losses} Lost ({((stats.losses / stats.settled) * 100).toFixed(0)}%)
                      </span>
                      <span className="text-gray-500">{stats.pushes} Push</span>
                    </div>
                    <div className="mt-4 space-y-1.5 border-t border-surface-4 pt-3">
                      {[
                        { label: 'Total Staked', value: formatCurrency(stats.totalStaked) },
                        {
                          label: 'Avg Odds',
                          value: `${stats.avgOdds > 0 ? '+' : ''}${stats.avgOdds.toFixed(0)}`,
                        },
                        { label: 'Yield', value: formatROI(stats.roi) },
                      ].map((r) => (
                        <div key={r.label} className="flex items-center justify-between text-xs">
                          <span className="text-gray-600">{r.label}</span>
                          <span className="font-mono text-gray-300">{r.value}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div className="lg:col-span-3 op-panel p-4">
                <div className="text-sm font-medium text-gray-300 mb-4">Bankroll Over Time</div>
                {histChart.length < 2 ? (
                  <div className="h-[180px] flex items-center justify-center text-xs text-gray-600">
                    Not enough data
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={180}>
                    <ComposedChart data={histChart}>
                      <defs>
                        <linearGradient id="bankrollGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 9, fill: '#6b7280' }}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 9, fill: '#6b7280' }}
                        tickLine={false}
                        axisLine={false}
                        domain={['auto', 'auto']}
                      />
                      <RechartTooltip
                        {...TT}
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const bal = payload[0]?.value as number;
                          const prev = (payload[0]?.payload as { prevBalance: number | null })
                            ?.prevBalance;
                          const dayDiff = prev != null ? bal - prev : null;
                          return (
                            <div style={TT.contentStyle} className="rounded p-2 space-y-1">
                              <div style={TT.labelStyle}>{label}</div>
                              <div className="text-gray-200">
                                Balance:{' '}
                                <span className="font-mono font-semibold">
                                  {formatCurrency(bal)}
                                </span>
                              </div>
                              {dayDiff != null && (
                                <div
                                  className={clsx(
                                    'font-mono text-xs',
                                    dayDiff >= 0 ? 'text-win' : 'text-loss',
                                  )}
                                >
                                  {dayDiff >= 0 ? '+' : ''}
                                  {formatCurrency(dayDiff)} vs prev day
                                </div>
                              )}
                              <div
                                className={clsx(
                                  'font-mono text-xs',
                                  bal >= startBal ? 'text-win' : 'text-loss',
                                )}
                              >
                                {bal >= startBal ? '+' : ''}
                                {formatCurrency(bal - startBal)} vs start
                              </div>
                            </div>
                          );
                        }}
                      />
                      <ReferenceLine y={startBal} stroke="#374151" strokeDasharray="4 2" />
                      <Area
                        type="monotone"
                        dataKey="balance"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        fill="url(#bankrollGrad)"
                        dot={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* ── Skill vs Luck ─────────────────── */}
            <SkillVsLuckChart bets={filtered} />

            {/* Market + Confidence */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <MarketChart
                byMarket={stats.byMarket}
                onFilter={handleMarketFilter}
                activeMarket={activeMarket}
              />
              <ConfidenceChart
                byConf={stats.byConf}
                onFilter={handleConfFilter}
                activeConf={activeConf}
              />
            </div>

            {/* Odds histogram */}
            {stats.oddsHist.length > 0 && <OddsHistogram data={stats.oddsHist} />}

            {/* ── Model Calibration ─────────────── */}
            <CalibrationPanel calibration={calibration} />
            <CalibrationHealthPanel metrics={runMetrics} />
            <WeightTuningPanel />

            {/* ── Decision Quality ──────────────── */}
            <DecisionQualityPanel bets={filtered} />

            {/* ── Strategy Simulator ───────────── */}
            <StrategySimulator bets={filtered} startBankroll={startBal} />

            {/* Insights */}
            <InsightsPanel bets={filtered} />

            {/* Model Intelligence — follows the high-confidence bets to show behavioral value */}
            <ModelIntelligencePanel stats={stats} allStats={allStats} />

            {/* Bet log with drill-through */}
            <BetTable bets={filtered} />
          </div>
        </div>
      )}
    </div>
  );
}
